import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { getMyFeatureAccess } from '../modules/platform/api/featureAccess';
import type { SchoolStudentFeatureKey } from '../modules/platform/types/featureAccess';
import { USER_ROLES } from '../utils/constants';
import { useAuth } from './AuthContext';

interface FeatureAccessContextType {
  /** True while auth is loading or school feature access is unresolved/fetching. */
  loading: boolean;
  /** Whether the feature-access request failed (gated modules treated as disabled). */
  error: boolean;
  tenantId: string | null;
  /** For mapped keys: true only when API returns true; missing/false/error → false (fail-closed). */
  isSchoolFeatureEnabled: (key: SchoolStudentFeatureKey) => boolean;
  /** vocabulary_pronunciation follows platform inherit rules (on when vocabulary is on unless explicitly off). */
  isVocabularyPronunciationEnabled: () => boolean;
  refetch: () => Promise<void>;
}

const FeatureAccessContext = createContext<FeatureAccessContextType | undefined>(undefined);

/** Roles that share school-level feature flags (sidebar, CMS, tutor integrations). */
const SCHOOL_FEATURE_ACCESS_ROLES: readonly string[] = [
  USER_ROLES.STUDENT,
  USER_ROLES.TEACHER,
  USER_ROLES.SCHOOL_ADMIN,
];

function userNeedsSchoolFeatureAccess(roles: string[] | undefined): boolean {
  if (!roles?.length) return false;
  return SCHOOL_FEATURE_ACCESS_ROLES.some((role) => roles.includes(role));
}

export const useFeatureAccess = (): FeatureAccessContextType => {
  const ctx = useContext(FeatureAccessContext);
  if (!ctx) {
    return {
      loading: false,
      error: false,
      tenantId: null,
      isSchoolFeatureEnabled: () => false,
      isVocabularyPronunciationEnabled: () => false,
      refetch: async () => {},
    };
  }
  return ctx;
};

interface FeatureAccessProviderProps {
  children: ReactNode;
}

export const FeatureAccessProvider: React.FC<FeatureAccessProviderProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id;
  const needsSchoolFeatureAccess = userNeedsSchoolFeatureAccess(user?.roles);

  const [fetchLoading, setFetchLoading] = useState(false);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [error, setError] = useState(false);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [accessMap, setAccessMap] = useState<Record<string, boolean> | null>(null);

  const membershipKey = user?.last_active_membership_key ?? user?.memberships?.[0]?.membership_key ?? '';

  const resetFeatureAccess = useCallback(() => {
    setAccessMap(null);
    setTenantId(null);
    setError(false);
    setFetchLoading(false);
    setHasFetchedOnce(false);
  }, []);

  const refetch = useCallback(async () => {
    if (!userId || !needsSchoolFeatureAccess) {
      resetFeatureAccess();
      return;
    }
    setFetchLoading(true);
    setError(false);
    try {
      const data = await getMyFeatureAccess();
      setTenantId(data.tenant_id ?? null);
      setAccessMap(data.school_feature_access ?? {});
    } catch (e) {
      console.error('Failed to load feature access:', e);
      setAccessMap(null);
      setTenantId(null);
      setError(true);
    } finally {
      setFetchLoading(false);
      setHasFetchedOnce(true);
    }
  }, [userId, needsSchoolFeatureAccess, resetFeatureAccess]);

  useEffect(() => {
    if (authLoading) return;
    if (!userId || !needsSchoolFeatureAccess) {
      resetFeatureAccess();
      return;
    }
    void refetch();
  }, [authLoading, userId, needsSchoolFeatureAccess, membershipKey, refetch, resetFeatureAccess]);

  const isSchoolFeatureEnabled = useCallback(
    (key: SchoolStudentFeatureKey): boolean => {
      if (!needsSchoolFeatureAccess || !hasFetchedOnce || !accessMap) return false;
      return accessMap[key] === true;
    },
    [needsSchoolFeatureAccess, hasFetchedOnce, accessMap]
  );

  const isVocabularyPronunciationEnabled = useCallback((): boolean => {
    if (!needsSchoolFeatureAccess || !hasFetchedOnce || !accessMap) return false;
    if (accessMap.vocabulary_pronunciation === true) return true;
    if (accessMap.vocabulary_pronunciation === false) return false;
    return accessMap.vocabulary === true;
  }, [needsSchoolFeatureAccess, hasFetchedOnce, accessMap]);

  /** Unresolved (pre-first-fetch) counts as loading so gates do not fail-closed redirect early. */
  const loading = authLoading || (needsSchoolFeatureAccess && (!hasFetchedOnce || fetchLoading));

  const value = useMemo(
    (): FeatureAccessContextType => ({
      loading,
      error,
      tenantId,
      isSchoolFeatureEnabled,
      isVocabularyPronunciationEnabled,
      refetch,
    }),
    [loading, error, tenantId, isSchoolFeatureEnabled, isVocabularyPronunciationEnabled, refetch]
  );

  return <FeatureAccessContext.Provider value={value}>{children}</FeatureAccessContext.Provider>;
};
